import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FilterBar,
  FilterField,
  Input,
  Pagination,
  RadixSelect,
  RowActions,
  Skeleton,
  StatusBadge,
  Badge,
  toast,
  type DataTableColumn,
  type SelectOption,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import {
  TEMPLATE_CHANNELS,
  TEMPLATE_LANGUAGES,
  type KakaoTemplateType,
  type TemplateApprovalStatus,
  type TemplateLanguageCode,
  type TemplateListItem,
} from '../types/notificationTemplate';
import type { NotificationChannel } from '../types/notificationMessage';
import {
  useNotificationTemplatesQuery,
  useDeleteNotificationTemplateMutation,
} from '../hooks/useNotificationTemplatesQuery';
import { NOTIFICATION_TEMPLATE_WRITE_ROLES } from '../lib/notificationActionRbac';
import { channelLabel, languageLabel } from '../lib/notificationLabels';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 250;
const ALL = '';

// 검수 상태 → StatusBadge variant (디자인 스펙 §4.1 색 의미 SSoT).
const APPROVAL_VARIANT: Record<TemplateApprovalStatus, 'success' | 'pending' | 'danger' | 'neutral'> = {
  approved: 'success',
  pending: 'pending',
  rejected: 'danger',
  draft: 'neutral',
};

const truncate = (input: string | null | undefined, max: number): string => {
  if (!input) return '';
  return input.length <= max ? input : `${input.slice(0, max)}…`;
};

const formatDate = (iso: string): string => {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return iso;
  }
};

/**
 * 템플릿 관리 탭 — 목록 + 검색/필터(채널·언어·유형·검수상태) + 행 액션(수정/삭제).
 * 등록/편집은 별도 풀페이지(/notifications/templates/new|:id). 기존 템플릿 hook 계약 재사용.
 */
export const TemplateTab = () => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const canWrite = useHasRole(NOTIFICATION_TEMPLATE_WRITE_ROLES);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [channel, setChannel] = useState<NotificationChannel | ''>('');
  const [languageCode, setLanguageCode] = useState<TemplateLanguageCode | ''>('');
  const [kakaoType, setKakaoType] = useState<KakaoTemplateType | ''>('');
  const [approval, setApproval] = useState<TemplateApprovalStatus | ''>('');
  const [page, setPage] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<TemplateListItem | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQ(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const filter = useMemo(
    () => ({
      ...(debouncedQ ? { q: debouncedQ } : {}),
      ...(channel ? { channel } : {}),
      ...(languageCode ? { languageCode } : {}),
      ...(kakaoType ? { kakaoTemplateType: kakaoType } : {}),
      ...(approval ? { approvalStatus: approval } : {}),
      page,
      size: PAGE_SIZE,
    }),
    [debouncedQ, channel, languageCode, kakaoType, approval, page],
  );

  const query = useNotificationTemplatesQuery(filter);
  const remove = useDeleteNotificationTemplateMutation();

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const handleReset = useCallback(() => {
    setSearchInput('');
    setDebouncedQ('');
    setChannel('');
    setLanguageCode('');
    setKakaoType('');
    setApproval('');
    setPage(0);
  }, []);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.templateId, {
      onSuccess: () => {
        toast.success(t('template.toast.deleteSuccess', '템플릿이 비활성화되었습니다.'));
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(err.message ?? t('template.toast.deleteFail', '템플릿 삭제에 실패했습니다.'));
      },
    });
  }, [deleteTarget, remove, t]);

  const channelOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL, label: t('common.all', '전체') },
      ...TEMPLATE_CHANNELS.map((c) => ({ value: c, label: channelLabel(t, c) })),
    ],
    [t],
  );
  const languageOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL, label: t('common.all', '전체') },
      ...TEMPLATE_LANGUAGES.map((l) => ({ value: l, label: languageLabel(t, l) })),
    ],
    [t],
  );
  const kakaoTypeOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL, label: t('common.all', '전체') },
      { value: 'basic', label: t('template.kakaoType.basic', '기본형') },
      { value: 'emphasis', label: t('template.kakaoType.emphasis', '강조표기형') },
      { value: 'image', label: t('template.kakaoType.image', '이미지형') },
    ],
    [t],
  );
  const approvalOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: ALL, label: t('common.all', '전체') },
      { value: 'draft', label: t('template.approval.draft', '작성 중') },
      { value: 'pending', label: t('template.approval.pending', '검수 대기') },
      { value: 'approved', label: t('template.approval.approved', '승인 완료') },
      { value: 'rejected', label: t('template.approval.rejected', '반려') },
    ],
    [t],
  );

  const columns: ReadonlyArray<DataTableColumn<TemplateListItem>> = useMemo(
    () => [
      {
        key: 'templateCode',
        header: t('template.col.templateCode', '템플릿 코드'),
        cell: (row) => (
          <Link
            to={`/notifications/templates/${row.templateId}`}
            className="font-mono text-[12px] font-medium underline-offset-2 hover:underline"
            style={{ color: 'var(--content-strong)' }}
          >
            {row.templateCode}
          </Link>
        ),
      },
      {
        key: 'channel',
        header: t('template.col.channel', '채널'),
        cell: (row) => <span className="text-[12px]">{channelLabel(t, row.channel)}</span>,
        width: 96,
      },
      {
        key: 'kakaoType',
        header: t('template.col.type', '유형'),
        cell: (row) =>
          row.kakaoTemplateType ? (
            <span className="text-[12px]" style={{ color: 'var(--content-default)' }}>
              {t(`template.kakaoType.${row.kakaoTemplateType}`)}
            </span>
          ) : (
            <span style={{ color: 'var(--content-muted)' }}>—</span>
          ),
        width: 96,
      },
      {
        key: 'languageCode',
        header: t('template.col.language', '언어'),
        cell: (row) => (
          <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {languageLabel(t, row.languageCode)}
          </span>
        ),
        width: 80,
      },
      {
        key: 'subject',
        header: t('template.col.subject', '제목'),
        cell: (row) => (
          <div className="flex items-center gap-1.5">
            <span
              className="block max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap text-[13px]"
              style={{ color: 'var(--content-default)' }}
              title={row.subject ?? ''}
            >
              {row.subject ? truncate(row.subject, 40) : '—'}
            </span>
            {/* ID-226 — EMAIL HTML 본문 템플릿 표식(plain 은 미표시, 회귀0). */}
            {row.channel === 'EMAIL' && row.isHtml ? (
              <Badge variant="info" size="sm">
                {t('template.flag.html', 'HTML')}
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: 'approval',
        header: t('template.col.approval', '검수 상태'),
        align: 'center',
        cell: (row) =>
          row.approvalStatus ? (
            <StatusBadge
              variant={APPROVAL_VARIANT[row.approvalStatus]}
              label={t(`template.approval.${row.approvalStatus}`)}
            />
          ) : (
            <span style={{ color: 'var(--content-muted)' }}>—</span>
          ),
        width: 100,
      },
      {
        key: 'isAdvertising',
        header: t('template.col.advertising', '광고'),
        align: 'center',
        cell: (row) =>
          row.isAdvertising ? (
            <Badge variant="warn" size="sm">
              {t('template.flag.advertising', '광고')}
            </Badge>
          ) : (
            <span style={{ color: 'var(--content-muted)' }}>—</span>
          ),
        width: 72,
      },
      {
        key: 'createdAt',
        header: t('template.col.createdAt', '등록일'),
        cell: (row) => (
          <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
            {formatDate(row.createdAt)}
          </span>
        ),
        width: 120,
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        width: 56,
        cell: (row) => (
          <RowActions
            triggerLabel={t('template.actions.trigger', '템플릿 액션')}
            actions={[
              {
                key: 'edit',
                label: t('template.actions.edit', '수정'),
                onClick: () => navigate(`/notifications/templates/${row.templateId}`),
              },
              ...(canWrite
                ? [
                    {
                      key: 'delete',
                      label: t('template.actions.delete', '삭제'),
                      variant: 'danger' as const,
                      separatorBefore: true,
                      onClick: () => setDeleteTarget(row),
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    [canWrite, navigate, t],
  );

  return (
    <div className="flex flex-col gap-4" data-testid="template-tab">
      <div className="flex items-center justify-end">
        {canWrite ? (
          <Link to="/notifications/templates/new">
            <Button size="sm">
              <Plus size={15} aria-hidden="true" />
              {t('template.actions.create', '템플릿 등록')}
            </Button>
          </Link>
        ) : null}
      </div>

      <FilterBar
        dense
        filters={
          <>
            <FilterField label={t('template.filter.q', '검색어')}>
              <Input
                type="search"
                size="sm"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('template.filter.qPlaceholder', '템플릿 코드·제목 검색')}
                aria-label={t('template.filter.q', '검색어')}
              />
            </FilterField>
            <FilterField label={t('template.filter.channel', '채널')}>
              <RadixSelect
                value={channel}
                onChange={(v) => {
                  setChannel(v as NotificationChannel | '');
                  setPage(0);
                }}
                options={channelOptions}
                aria-label={t('template.filter.channel', '채널')}
              />
            </FilterField>
            <FilterField label={t('template.kakaoType.label', '알림톡 유형')}>
              <RadixSelect
                value={kakaoType}
                onChange={(v) => {
                  setKakaoType(v as KakaoTemplateType | '');
                  setPage(0);
                }}
                options={kakaoTypeOptions}
                aria-label={t('template.kakaoType.label', '알림톡 유형')}
              />
            </FilterField>
            <FilterField label={t('template.approval.label', '검수 상태')}>
              <RadixSelect
                value={approval}
                onChange={(v) => {
                  setApproval(v as TemplateApprovalStatus | '');
                  setPage(0);
                }}
                options={approvalOptions}
                aria-label={t('template.approval.label', '검수 상태')}
              />
            </FilterField>
            <FilterField label={t('template.filter.language', '언어')}>
              <RadixSelect
                value={languageCode}
                onChange={(v) => {
                  setLanguageCode(v as TemplateLanguageCode | '');
                  setPage(0);
                }}
                options={languageOptions}
                aria-label={t('template.filter.language', '언어')}
              />
            </FilterField>
          </>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              {t('common.search', '검색')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset}>
              {t('common.reset', '초기화')}
            </Button>
          </>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[36px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('template.error.listLoad', '템플릿 목록을 불러오지 못했습니다.')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('common.retry', '다시 시도')}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={t('template.empty', '등록된 템플릿이 없습니다.')}
          action={
            canWrite ? (
              <Link to="/notifications/templates/new">
                <Button size="sm">
                  <Plus size={15} aria-hidden="true" />
                  {t('template.actions.create', '템플릿 등록')}
                </Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...items]}
          rowKey={(row) => row.templateId}
          stickyHeader
          mobileCardCollapse
          ariaLabel={t('template.title', '템플릿 관리')}
        />
      )}

      <Pagination
        total={total}
        currentPage={page + 1}
        pageSize={PAGE_SIZE}
        onPageChange={(p) => setPage(p - 1)}
        className="mt-1"
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('template.deleteDialog.title', '템플릿 삭제')}</DialogTitle>
            <DialogDescription>
              {t(
                'template.deleteDialog.description',
                '정말 삭제하시겠습니까? 발송 이력은 유지되지만 이후 발송에는 사용할 수 없습니다.',
              )}
            </DialogDescription>
          </DialogHeader>
          {deleteTarget ? (
            <p className="font-mono text-[12px]" style={{ color: 'var(--content-muted)' }}>
              {deleteTarget.templateCode} · {channelLabel(t, deleteTarget.channel)} ·{' '}
              {languageLabel(t, deleteTarget.languageCode)}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('common.cancel', '취소')}
              </Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={remove.isPending} onClick={handleDelete}>
              {remove.isPending
                ? t('common.deleting', '삭제 중…')
                : t('common.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
